import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, X } from "lucide-react";
import SEO from "@/components/SEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      // Filter to only show images (not PDFs or other files)
      const imageFiles = (res.data || []).filter(item => 
        item.url && (
          item.url.toLowerCase().endsWith('.jpg') ||
          item.url.toLowerCase().endsWith('.jpeg') ||
          item.url.toLowerCase().endsWith('.png') ||
          item.url.toLowerCase().endsWith('.webp') ||
          item.url.toLowerCase().endsWith('.gif') ||
          item.url.includes('unsplash') ||
          item.url.includes('customer-assets')
        )
      );
      setImages(imageFiles);
    } catch (e) {
      console.error("Error fetching images:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO 
        title="Photo Gallery"
        description="Explore photos of Chestnut Hill Inn, our beautiful rooms, scenic porches, and the stunning beaches of Long Island, Maine."
        canonical="/gallery"
      />
      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917] transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          <a href="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </a>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white text-xs uppercase tracking-widest"
          >
            Book Now
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#134E4A] py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Photo Gallery
          </h1>
          <p className="text-white/70 text-lg">
            Explore the beauty of Chestnut Hill Inn and Long Island, Maine
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#78716C]">Loading photos...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#78716C]">No photos available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image, index) => (
                <div
                  key={image.id || index}
                  className="relative overflow-hidden cursor-pointer group bg-stone-100"
                  style={{ aspectRatio: '4/3' }}
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.url}
                    alt={image.alt_text || image.caption || `Chestnut Hill Inn photo ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="absolute inset-0 flex items-center justify-center bg-stone-200 text-stone-400">Image unavailable</div>';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                  {image.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-sm">{image.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-white/80 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.caption || "Chestnut Hill Inn"}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedImage.caption && (
            <p className="absolute bottom-8 left-0 right-0 text-center text-white text-lg">
              {selectedImage.caption}
            </p>
          )}
        </div>
      )}

      {/* Floor Plans Section */}
      <section className="py-16 px-6 bg-white border-t border-stone-200">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl text-[#1C1917] mb-2 text-center">Floor Plans</h2>
          <p className="text-[#78716C] text-center mb-8">View our detailed room layouts</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <a
              href="https://customer-assets.emergentagent.com/job_eventlodgeapp/artifacts/03trf8wy_209%20Upstairs.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-6 bg-[#FAFAF9] rounded-lg border border-stone-200 hover:border-[#134E4A] hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-[#134E4A] rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-[#1C1917] group-hover:text-[#134E4A] transition-colors">Upstairs Bedroom Layout</h3>
                <p className="text-sm text-[#78716C]">7 Bedrooms • 4 Queen Suites • 5 Bathrooms</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-[#F5F5F4]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-[#1C1917] mb-4">
            Ready to Experience It Yourself?
          </h2>
          <p className="text-[#78716C] mb-8">
            Book your stay at Chestnut Hill Inn and create your own memories on Long Island.
          </p>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white px-8 py-6 text-sm uppercase tracking-widest"
          >
            Book Your Stay
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-stone-200">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-4 mb-4">
            <a
              href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/chestnuthillinn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#78716C] hover:text-[#134E4A] transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
          <p className="text-[#78716C] text-sm">
            © {new Date().getFullYear()} Chestnut Hill Inn. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
